import { useEffect, useState } from 'react';
import { funnelPayload } from '@appTypes/gtmFunnel';
import type { EventPayload } from '@appTypes/GTM/EventPayload';
import useSendEspialCheckoutFunnelEventOnce from '@hooks/Espial/useSendEspialCheckoutFunnelEventOnce';
import { useSendGTMEventOnce } from '@hooks/GTM/useSendGTMEventOnce';
import { ProductID } from '@lib/ProductID';

const CHECKOUT_NAME = 'internasjonalt-forerkort';

export enum IDPCheckoutStepName {
  DrivingPermit = 'Type førerkort',
  Holder = 'Personalia',
  Documents = 'Vedlegg',
  Consents = 'Samtykker',
  Payment = 'Betaling',
  Receipt = 'Kvittering',
}

const getProductId = (productId?: string): string | undefined => {
  if (productId === ProductID.Idp1Year || productId === ProductID.Idp3Year || productId === ProductID.Idp1and3Year) {
    return productId;
  }
  return undefined;
};

const getCheckoutStepName = (checkoutStep: number, stepName: IDPCheckoutStepName) => `${checkoutStep} - ${stepName}`;

export const useTrackingStep1 = (productId?: string) => {
  const CHECKOUT_STEP = 1;
  const validProductId = getProductId(productId);
  const checkoutStepName = getCheckoutStepName(CHECKOUT_STEP, IDPCheckoutStepName.DrivingPermit);

  const gtmPayload: EventPayload & { checkout_step_name?: string } = {
    ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
    checkout_step_name: checkoutStepName,
  } as const;

  const espialPayload = [
    { checkout_name: CHECKOUT_NAME },
    { checkout_step: CHECKOUT_STEP },
    { checkout_step_name: checkoutStepName },
    ...(validProductId ? [{ checkout_product: validProductId }] : []),
  ];

  useSendGTMEventOnce(gtmPayload, []);
  useSendEspialCheckoutFunnelEventOnce(espialPayload, []);
};

export const useTrackingStep2 = (productId?: string) => {
  const CHECKOUT_STEP = 2;
  const validProductId = getProductId(productId);
  const checkoutStepName = getCheckoutStepName(CHECKOUT_STEP, IDPCheckoutStepName.Holder);

  useSendGTMEventOnce(
    validProductId &&
      ({
        ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
        checkout_step_name: checkoutStepName,
      } as EventPayload & { checkout_step_name?: string }),
    [validProductId],
  );
  useSendEspialCheckoutFunnelEventOnce(
    validProductId && [
      { checkout_name: CHECKOUT_NAME },
      { checkout_step: CHECKOUT_STEP },
      { checkout_step_name: checkoutStepName },
      { checkout_product: validProductId },
    ],
    [validProductId],
  );
};

export const useTrackingStep3 = (productId?: string) => {
  const CHECKOUT_STEP = 3;
  const validProductId = getProductId(productId);
  const checkoutStepName = getCheckoutStepName(CHECKOUT_STEP, IDPCheckoutStepName.Documents);

  useSendGTMEventOnce(
    validProductId &&
      ({
        ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
        checkout_step_name: checkoutStepName,
      } as EventPayload & { checkout_step_name?: string }),
    [validProductId],
  );
  useSendEspialCheckoutFunnelEventOnce(
    validProductId && [
      { checkout_name: CHECKOUT_NAME },
      { checkout_step: CHECKOUT_STEP },
      { checkout_step_name: checkoutStepName },
      { checkout_product: validProductId },
    ],
    [validProductId],
  );
};

export const useTrackingStep4 = (productId?: string) => {
  const CHECKOUT_STEP = 4;
  const validProductId = getProductId(productId);
  const checkoutStepName = getCheckoutStepName(CHECKOUT_STEP, IDPCheckoutStepName.Consents);

  useSendGTMEventOnce(
    validProductId &&
      ({
        ...funnelPayload(CHECKOUT_NAME, CHECKOUT_STEP),
        checkout_step_name: checkoutStepName,
      } as EventPayload & { checkout_step_name?: string }),
    [validProductId],
  );
  useSendEspialCheckoutFunnelEventOnce(
    validProductId && [
      { checkout_name: CHECKOUT_NAME },
      { checkout_step: CHECKOUT_STEP },
      { checkout_step_name: checkoutStepName },
      { checkout_product: validProductId },
    ],
    [validProductId],
  );
};

const useTrackedStepStatus = (productId: string | undefined, checkoutStep: number, stepName: IDPCheckoutStepName) => {
  const validProductId = getProductId(productId);
  const [status, setStatus] = useState<'pending' | 'done' | 'error'>('pending');
  const [gtmDone, setGtmDone] = useState(false);
  const [espialDone, setEspialDone] = useState(false);
  const checkoutStepName = getCheckoutStepName(checkoutStep, stepName);

  useSendGTMEventOnce(
    validProductId &&
      ({
        ...funnelPayload(CHECKOUT_NAME, checkoutStep),
        checkout_step_name: checkoutStepName,
      } as EventPayload & { checkout_step_name?: string }),
    [],
    () => setGtmDone(true),
    () => setStatus('error'),
  );

  useSendEspialCheckoutFunnelEventOnce(
    validProductId && [
      { checkout_name: CHECKOUT_NAME },
      { checkout_step: checkoutStep },
      { checkout_step_name: checkoutStepName },
      { checkout_product: validProductId },
    ],
    [validProductId],
    () => setEspialDone(true),
    () => setStatus('error'),
  );

  useEffect(() => {
    if (gtmDone && espialDone && status === 'pending') {
      setStatus('done');
    }
  }, [gtmDone, espialDone, status]);

  return status;
};

export const useTrackingPaymentStep = (productId?: string, checkoutStep = 4) =>
  useTrackedStepStatus(productId, checkoutStep, IDPCheckoutStepName.Payment);

export const useTrackingReceiptStep = (productId?: string | number, checkoutStep = 5) =>
  useTrackedStepStatus(productId ? String(productId) : undefined, checkoutStep, IDPCheckoutStepName.Receipt);
